import React, { useEffect, useMemo, useRef, useState } from "react";
import { fetchManagerSeasonDriverOptions, fetchManagerSeasonRaces, fetchManagerSeriesSeasons } from "../../../lib/managerApi";
import { sortDriversByCarNumber } from "../../../lib/driverSorting.js";
import {
    analyzeIracecontrolPdf,
    fetchResultsImportDbRows,
    getJob,
    runOfftrackRecalculationDb,
    runOrgaPenaltyDb,
    runResultsImportDb,
    runStandingsSnapshotDb,
    saveResultsImportManualOverrides,
    streamJobLogs,
    uploadIracingJson,
} from "../../../lib/ligamanagerApi";

function prettyErr(error) {
    if (!error) return "Unbekannter Fehler";
    if (typeof error === "string") return error;
    if (error instanceof Error) return error.message || "Fehler";
    if (typeof error === "object") {
        if (error.detail) return typeof error.detail === "string" ? error.detail : JSON.stringify(error.detail);
        if (error.message) return error.message;
    }
    return String(error);
}

function ProgressBar({ progress }) {
    const total = Number(progress?.total || 0);
    const step = Number(progress?.step || 0);
    const percent = total > 0 ? Math.round((Math.max(0, step) / Math.max(1, total)) * 100) : 0;

    if (!progress?.status && !total) return null;

    return (
        <div style={{ width: "100%", marginTop: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", opacity: 0.85, fontFamily: "var(--ff-Everett)", fontSize: 12 }}>
                <div>{progress?.label || progress?.status || "Status"}</div>
                <div>{total > 0 ? `${step}/${total} - ${percent}%` : progress?.status}</div>
            </div>
            <div
                style={{
                    marginTop: 6,
                    height: 10,
                    borderRadius: 999,
                    border: "1px solid rgba(232,253,83,.35)",
                    background: "rgba(232,253,83,.08)",
                    overflow: "hidden",
                }}
            >
                <div
                    style={{
                        height: "100%",
                        width: `${percent}%`,
                        background: "rgba(232,253,83,.75)",
                    }}
                />
            </div>
        </div>
    );
}

function parseGapSeconds(value) {
    const raw = String(value || "").trim();
    if (!raw) return null;
    if (raw.toUpperCase().includes("L")) return null;
    const normalized = raw.startsWith("+") ? raw.slice(1) : raw;
    const safe = normalized.replace(",", ".");
    const parts = safe.split(":");
    try {
        if (parts.length === 1) return Number(parts[0]);
        if (parts.length === 2) return Number(parts[0]) * 60 + Number(parts[1]);
        if (parts.length === 3) return Number(parts[0]) * 3600 + Number(parts[1]) * 60 + Number(parts[2]);
    } catch {}
    return null;
}

function formatGapSeconds(seconds) {
    const sec = Math.max(Number(seconds || 0), 0);
    const minutes = Math.floor(sec / 60);
    const rest = sec - minutes * 60;
    return `${minutes}:${rest.toFixed(3).padStart(6, "0")}`;
}

function normalizeIntervalDisplay(value) {
    const raw = String(value || "").trim();
    if (!raw) return "";
    const manualPrefix = /^manual:\s*/i;
    if (manualPrefix.test(raw)) {
        const jsonCandidate = raw.replace(manualPrefix, "").trim();
        try {
            const payload = JSON.parse(jsonCandidate);
            const fromJson = payload?.override_interval_display_overall || payload?.override_interval_display || payload?.final_interval_display || "";
            return normalizeIntervalDisplay(fromJson);
        } catch {}
    }
    if (raw.toUpperCase().includes("L")) return raw.toUpperCase();
    const sec = parseGapSeconds(raw);
    if (sec === null || Number.isNaN(sec)) return raw;
    return formatGapSeconds(sec);
}

function driverDisplayLabel(row) {
    const driverName = row?.driver_label || row?.driver?.display_name || row?.driver?.iracing_name || `Saisonfahrer #${row?.id}`;
    const carNumber = row?.car_number ? `#${row.car_number}` : "";
    const cls = row?.class_name ? `(${row.class_name})` : "";
    return [carNumber, driverName, cls].filter(Boolean).join(" ");
}

function driverNameForSort(row) {
    return String(row?.driver_label || row?.driver?.display_name || row?.driver?.iracing_name || "").trim();
}

const ORGA_PENALTY_TYPE_OPTIONS = [
    { value: "TIME_PENALTY", label: "Zeitstrafe" },
    { value: "TIME_CREDIT", label: "Zeitgutschrift / iRacing-Korrektur" },
    { value: "POINTS_PENALTY", label: "Punktestrafe" },
    { value: "TIME_AND_POINTS", label: "Zeit + Punkte" },
    { value: "DSQ", label: "DSQ" },
    { value: "DQ", label: "DQ" },
];

function normalizeJobSummary(value) {
    if (!value) return null;

    if (typeof value === "string") {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }

    if (typeof value === "object") {
        return value;
    }

    return null;
}

function extractUnexcusedAbsencesFromLogs(logItems) {
    const rows = [];

    for (const item of logItems || []) {
        const msg = String(item?.msg || item?.message || "").trim();

        const match = msg.match(
            /\[DB Results\]\s+\d+\.\s+([PW]\d+)\s+-\s+(?:#([^\s]+)\s+)?(.+?)\s+\/\s+Klasse:\s*(.*?)\s+\/\s+Team:\s*(.*?)\s+\/\s+(.*)$/i
        );

        if (!match) continue;

        const gridLabel = match[1] || "";
        const carNumber = match[2] || "";
        const driverName = match[3] || "Fahrer";
        const className = match[4] || "-";
        const teamName = match[5] || "-";
        const statusText = match[6] || "";

        const gridPosition = Number(gridLabel.replace(/\D/g, "") || 0);
        const starterType = gridLabel.startsWith("W") ? "waitlist" : "regular";

        rows.push({
            season_driver_id: `log-${rows.length + 1}`,
            driver_id: null,
            driver_name: driverName.trim(),
            car_number: carNumber.trim(),
            class_name: className.trim(),
            team_name: teamName.trim(),
            grid_position: gridPosition,
            starter_type: starterType,
            reason: statusText.trim(),
        });
    }

    return rows;
}

function normalizeDriverKey(value) {
    return String(value || "").trim().toUpperCase().replace(/\s+/g, " ");
}

function normalizeCarNumber(value) {
    const raw = String(value ?? "").replace("#", "").trim();
    if (/^\d+$/.test(raw)) return String(Number(raw));
    return raw.toUpperCase();
}

function pdfIncidentStatus(row) {
    if (!row?.pdf_loaded) return { label: "-", tone: "muted" };
    if (!row?.pdf_matched) return { label: "Kein PDF-Match", tone: "warn" };
    if (!row?.pdf_auto_safe) return { label: "Manuell prüfen", tone: "warn" };
    if (row?.pdf_total_matches_json === false) {
        return {
            label: `Abweichung PDF ${row.pdf_total_incident_points ?? "-"} / JSON ${row.total_incidents ?? "-"}`,
            tone: "warn",
        };
    }
    return { label: "PDF geprüft", tone: "ok" };
}

function incidentRowMatchKey(row) {
    const carNumber = String(row?.car_number ?? row?.car_number_snapshot ?? "").replace("#", "").trim();
    const driverName = normalizeDriverKey(row?.driver_name ?? row?.driver_name_snapshot ?? "");
    return `${carNumber}|${driverName}`;
}

async function parseRaceIncidentRowsFromFile(file) {
    if (!file) return [];

    const raw = await file.text();
    const json = JSON.parse(raw);
    const root = json?.data && typeof json.data === "object" ? json.data : json;
    const sessions = Array.isArray(root?.session_results) ? root.session_results : [];
    const raceSessions = sessions.filter(
        (session) => String(session?.simsession_name || "").trim().toUpperCase() === "RACE",
    );

    const rows = [];
    raceSessions.forEach((session, sessionIndex) => {
        const results = Array.isArray(session?.results) ? session.results : [];
        results.forEach((result, rowIndex) => {
            const carNumber = String(result?.livery?.car_number ?? result?.car_number ?? "").replace("#", "").trim();
            const driverName = String(result?.display_name || result?.driver_name || "").trim() || `Fahrer ${rowIndex + 1}`;
            const totalIncidents = Math.max(Number(result?.incidents || 0) || 0, 0);

            rows.push({
                preview_key: `${sessionIndex}-${result?.cust_id ?? driverName}-${carNumber}-${rowIndex}`,
                session_result_id: null,
                driver_name: driverName,
                car_number: carNumber,
                total_incidents: totalIncidents,
                non_offtrack_incidents: "0",
                offtracks: null,
                offtrack_penalty_seconds: null,
            });
        });
    });

    return sortDriversByCarNumber(rows);
}

export default function ResultsImport() {
    const [seriesRows, setSeriesRows] = useState([]);
    const [seriesKey, setSeriesKey] = useState("gt3x");
    const [seasonId, setSeasonId] = useState("");
    const [races, setRaces] = useState([]);
    const [raceId, setRaceId] = useState("");

    const [file, setFile] = useState(null);
    const [uploadId, setUploadId] = useState("");
    const [uploadName, setUploadName] = useState("");
    const [jobId, setJobId] = useState(null);
    const [summary, setSummary] = useState(null);
    const [manualRows, setManualRows] = useState([]);
    const [manualSummary, setManualSummary] = useState(null);
    const [incidentRows, setIncidentRows] = useState([]);
    const [incidentCalculated, setIncidentCalculated] = useState(false);
    const [offtrackSummary, setOfftrackSummary] = useState(null);
    const [incidentPdfFile, setIncidentPdfFile] = useState(null);
    const [incidentPdfSummary, setIncidentPdfSummary] = useState(null);
    const [seasonDriverOptions, setSeasonDriverOptions] = useState([]);
    const [orgaPenaltyDriverId, setOrgaPenaltyDriverId] = useState("");
    const [orgaPenaltyType, setOrgaPenaltyType] = useState("TIME_PENALTY");
    const [orgaPenaltySeconds, setOrgaPenaltySeconds] = useState("20");
    const [orgaPenaltyPoints, setOrgaPenaltyPoints] = useState("0");
    const [orgaPenaltyReason, setOrgaPenaltyReason] = useState("");
    const [orgaPenaltySummary, setOrgaPenaltySummary] = useState(null);

    const [logItems, setLogItems] = useState([]);
    const [progress, setProgress] = useState({ status: "", step: 0, total: 0, label: "" });
    const [error, setError] = useState("");
    const [loadingMeta, setLoadingMeta] = useState(false);
    const [uploadBusy, setUploadBusy] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [standingsRefreshBusy, setStandingsRefreshBusy] = useState(false);
    const [manualLoadBusy, setManualLoadBusy] = useState(false);
    const [manualSaveBusy, setManualSaveBusy] = useState(false);
    const [incidentFileBusy, setIncidentFileBusy] = useState(false);
    const [incidentPdfBusy, setIncidentPdfBusy] = useState(false);
    const [offtrackBusy, setOfftrackBusy] = useState(false);
    const [orgaPenaltyBusy, setOrgaPenaltyBusy] = useState(false);
    const [seasonDriverLoadBusy, setSeasonDriverLoadBusy] = useState(false);

    const stopStreamRef = useRef(null);
    const pollTimerRef = useRef(null);
    const logsRef = useRef(null);

    const selectedSeries = useMemo(
        () => seriesRows.find((row) => row.series_key === seriesKey) || null,
        [seriesRows, seriesKey],
    );
    const seasons = useMemo(() => selectedSeries?.seasons || [], [selectedSeries]);
    const selectedSeason = useMemo(
        () => seasons.find((season) => String(season.id) === String(seasonId)) || null,
        [seasons, seasonId],
    );
    const selectedRace = useMemo(
        () => races.find((race) => String(race.id) === String(raceId)) || null,
        [races, raceId],
    );
    const sortedSeasonDriverOptions = useMemo(
        () => sortDriversByCarNumber(seasonDriverOptions),
        [seasonDriverOptions],
    );

    const unexcusedAbsences = useMemo(() => {
        const normalizedSummary = normalizeJobSummary(summary) || {};

        if (Array.isArray(normalizedSummary.unexcused_absences)) {
            return normalizedSummary.unexcused_absences;
        }

        if (Array.isArray(normalizedSummary.absence_audit?.unexcused_absences)) {
            return normalizedSummary.absence_audit.unexcused_absences;
        }

        if (Array.isArray(normalizedSummary.summary_json?.unexcused_absences)) {
            return normalizedSummary.summary_json.unexcused_absences;
        }

        if (Array.isArray(normalizedSummary.summary_json?.absence_audit?.unexcused_absences)) {
            return normalizedSummary.summary_json.absence_audit.unexcused_absences;
        }

        return extractUnexcusedAbsencesFromLogs(logItems);
    }, [summary, logItems]);

    const absenceAuditAvailable = useMemo(() => {
        const normalizedSummary = normalizeJobSummary(summary) || {};
        const logFallbackRows = extractUnexcusedAbsencesFromLogs(logItems);

        return Boolean(
            summary ||
            logFallbackRows.length ||
            normalizedSummary.unexcused_absence_count !== undefined ||
            normalizedSummary.absence_audit?.checked === true ||
            normalizedSummary.absence_audit?.checked === false ||
            normalizedSummary.absence_audit?.unexcused_absence_count !== undefined
        );
    }, [summary, logItems]);

    const busy = loadingMeta || uploadBusy || importBusy || standingsRefreshBusy || manualLoadBusy || manualSaveBusy || incidentFileBusy || incidentPdfBusy || offtrackBusy || orgaPenaltyBusy || seasonDriverLoadBusy;
    const fileLabel = file ? file.name : "JSON auswaehlen...";
    const sectionStyle = {
        border: "1px solid var(--border-soft)",
        borderRadius: 18,
        padding: 16,
        background: "rgba(232,253,83,.045)",
    };

    function pushUi(msg) {
        setLogItems((prev) => [...prev, { level: "INFO", msg: String(msg || ""), uiOnly: true }]);
    }

    function pushLog(item) {
        setLogItems((prev) => [
            ...prev,
            {
                seq: typeof item?.seq === "number" ? item.seq : null,
                level: String(item?.level || "INFO").toUpperCase(),
                ts: item?.ts || null,
                msg: item?.msg ?? "",
                uiOnly: false,
            },
        ]);
    }

    function stopStream() {
        try {
            if (stopStreamRef.current) stopStreamRef.current();
        } catch {}
        stopStreamRef.current = null;
    }

    function stopPolling() {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }

    async function pollJob(id) {
        const job = await getJob(id);

        setProgress({
            status: job?.status || "",
            step: Number(job?.progress_step || 0),
            total: Number(job?.progress_total || 0),
            label: job?.progress_label || "",
        });

        const nextSummary = normalizeJobSummary(
            job?.summary_json ??
            job?.summaryJson ??
            job?.summary ??
            null
        );

        if (nextSummary) {
            setSummary(nextSummary);
        }

        if (["done", "error", "canceled"].includes(String(job?.status || ""))) {
            stopPolling();
            setImportBusy(false);
            setStandingsRefreshBusy(false);
        }
    }

    function startPolling(id) {
        stopPolling();
        pollJob(id).catch(() => {});
        pollTimerRef.current = setInterval(() => {
            pollJob(id).catch(() => {});
        }, 800);
    }

    useEffect(() => {
        let active = true;

        async function load() {
            setLoadingMeta(true);
            setError("");
            try {
                const data = await fetchManagerSeriesSeasons();
                if (!active) return;

                const rows = Array.isArray(data?.series) ? data.series : [];
                setSeriesRows(rows);

                const preferred = rows.find((row) => row.series_key === "gt3x") || rows[0] || null;
                if (preferred) {
                    setSeriesKey(preferred.series_key);
                    const activeSeason = (preferred.seasons || []).find((season) => season.status === "active");
                    const nextSeason = activeSeason || (preferred.seasons || [])[0] || null;
                    setSeasonId(nextSeason ? String(nextSeason.id) : "");
                }
            } catch (err) {
                if (active) setError(prettyErr(err));
            } finally {
                if (active) setLoadingMeta(false);
            }
        }

        load();
        return () => {
            active = false;
            stopStream();
            stopPolling();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!seasonId) {
            setRaces([]);
            setRaceId("");
            return;
        }

        let active = true;

        async function loadRaces() {
            setLoadingMeta(true);
            setError("");
            try {
                const data = await fetchManagerSeasonRaces(seasonId);
                if (!active) return;

                const nextRaces = Array.isArray(data?.races) ? data.races : [];
                const nextRaceId =
                    data?.current_race_id ||
                    data?.selected_race_id ||
                    nextRaces[0]?.id ||
                    "";

                setRaces(nextRaces);
                setRaceId(nextRaceId ? String(nextRaceId) : "");
            } catch (err) {
                if (active) setError(prettyErr(err));
            } finally {
                if (active) setLoadingMeta(false);
            }
        }

        loadRaces();
        return () => {
            active = false;
        };
    }, [seasonId]);

    useEffect(() => {
        if (!seasonId) {
            setSeasonDriverOptions([]);
            setOrgaPenaltyDriverId("");
            return;
        }

        let active = true;

        async function loadSeasonDrivers() {
            setSeasonDriverLoadBusy(true);
            try {
                const data = await fetchManagerSeasonDriverOptions({ season_id: Number(seasonId) });
                if (!active) return;

                const rows = Array.isArray(data?.season_drivers) ? data.season_drivers : [];
                setSeasonDriverOptions(rows);
                const sortedRows = [...rows].sort((a, b) => {
                    const aName = driverNameForSort(a);
                    const bName = driverNameForSort(b);
                    const aFirst = (aName.split(/\s+/)[0] || "").toLowerCase();
                    const bFirst = (bName.split(/\s+/)[0] || "").toLowerCase();
                    const firstCmp = aFirst.localeCompare(bFirst, "de");
                    if (firstCmp !== 0) return firstCmp;
                    return aName.localeCompare(bName, "de");
                });

                if (!sortedRows.length) {
                    setOrgaPenaltyDriverId("");
                    return;
                }

                setOrgaPenaltyDriverId((prev) => {
                    const exists = sortedRows.some((row) => String(row.id) === String(prev));
                    return exists ? prev : String(sortedRows[0].id);
                });
            } catch (err) {
                if (!active) return;
                setSeasonDriverOptions([]);
                setOrgaPenaltyDriverId("");
                setError(prettyErr(err));
            } finally {
                if (active) setSeasonDriverLoadBusy(false);
            }
        }

        loadSeasonDrivers();
        return () => {
            active = false;
        };
    }, [seasonId]);

    useEffect(() => {
        const el = logsRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, [logItems]);

    useEffect(() => {
        setManualRows([]);
        setManualSummary(null);
        if (!selectedSeason || !selectedRace) return;
        loadManualRows().catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedSeason?.id, selectedRace?.id, summary?.result_import_id]);

    function onSeriesChange(nextSeriesKey) {
        setSeriesKey(nextSeriesKey);
        const nextSeries = seriesRows.find((row) => row.series_key === nextSeriesKey);
        const activeSeason = (nextSeries?.seasons || []).find((season) => season.status === "active");
        const nextSeason = activeSeason || (nextSeries?.seasons || [])[0] || null;
        setSeasonId(nextSeason ? String(nextSeason.id) : "");
        setRaceId("");
        setRaces([]);
    }

    async function onFileChange(nextFile) {
        setFile(nextFile);
        setUploadId("");
        setUploadName("");
        setOfftrackSummary(null);
        setIncidentCalculated(false);
        setIncidentPdfFile(null);
        setIncidentPdfSummary(null);

        if (!nextFile) {
            setIncidentRows([]);
            return;
        }

        setIncidentFileBusy(true);
        try {
            const rows = await parseRaceIncidentRowsFromFile(nextFile);
            setIncidentRows(rows);
            if (!rows.length) {
                setError('In der JSON wurde keine Session mit simsession_name "RACE" gefunden.');
            } else {
                setError("");
            }
        } catch (err) {
            setIncidentRows([]);
            setError(`JSON konnte fuer die Incident-Pruefung nicht gelesen werden: ${prettyErr(err)}`);
        } finally {
            setIncidentFileBusy(false);
        }
    }

    async function onIncidentPdfChange(nextFile) {
        setIncidentPdfFile(nextFile || null);
        setIncidentPdfSummary(null);
        setOfftrackSummary(null);
        setIncidentCalculated(false);

        if (!nextFile) {
            setIncidentRows((prev) =>
                prev.map((row) => {
                    const cleaned = { ...row };
                    Object.keys(cleaned).forEach((key) => {
                        if (key.startsWith("pdf_")) delete cleaned[key];
                    });
                    return cleaned;
                }),
            );
            return;
        }

        if (!incidentRows.length) {
            setError("Bitte zuerst die iRacing JSON laden oder vorhandene Result Rows laden.");
            return;
        }

        setError("");
        setIncidentPdfBusy(true);
        try {
            const data = await analyzeIracecontrolPdf(nextFile);
            const pdfRows = Array.isArray(data?.drivers) ? data.drivers : [];

            const byCar = new Map();
            const byName = new Map();
            pdfRows.forEach((row) => {
                const carKey = normalizeCarNumber(row?.car_number);
                const nameKey = normalizeDriverKey(row?.driver_name);
                if (carKey) byCar.set(carKey, row);
                if (nameKey) {
                    const list = byName.get(nameKey) || [];
                    list.push(row);
                    byName.set(nameKey, list);
                }
            });

            let matched = 0;
            let autoApplied = 0;
            let mismatches = 0;
            let manualReview = 0;
            let unmatched = 0;

            const nextIncidentRows = incidentRows.map((row) => {
                const carKey = normalizeCarNumber(row?.car_number);
                const nameKey = normalizeDriverKey(row?.driver_name);
                let pdfRow = carKey ? byCar.get(carKey) : null;
                if (!pdfRow && nameKey) {
                    const nameMatches = byName.get(nameKey) || [];
                    if (nameMatches.length === 1) pdfRow = nameMatches[0];
                }

                if (!pdfRow) {
                    unmatched += 1;
                    return {
                        ...row,
                        pdf_loaded: true,
                        pdf_matched: false,
                    };
                }

                matched += 1;
                const total = Math.max(Number(row.total_incidents || 0) || 0, 0);
                const pdfTotal = Math.max(Number(pdfRow.pdf_total_incident_points || 0) || 0, 0);
                const totalMatches = pdfTotal === total;
                const autoSafe = Boolean(pdfRow.auto_safe);
                const suggestedDeduction = Math.max(Number(pdfRow.pdf_non_offtrack_incidents || 0) || 0, 0);
                const canApply = autoSafe && totalMatches && suggestedDeduction <= total;

                if (!totalMatches) mismatches += 1;
                if (!autoSafe) manualReview += 1;
                if (canApply) autoApplied += 1;

                return {
                    ...row,
                    pdf_loaded: true,
                    pdf_matched: true,
                    pdf_auto_safe: autoSafe,
                    pdf_total_matches_json: totalMatches,
                    pdf_1x: Number(pdfRow.pdf_1x || 0),
                    pdf_2x: Number(pdfRow.pdf_2x || 0),
                    pdf_3x: Number(pdfRow.pdf_3x || 0),
                    pdf_4x: Number(pdfRow.pdf_4x || 0),
                    pdf_1x_to_2x: Number(pdfRow.pdf_1x_to_2x || 0),
                    pdf_1x_to_4x: Number(pdfRow.pdf_1x_to_4x || 0),
                    pdf_2x_to_4x: Number(pdfRow.pdf_2x_to_4x || 0),
                    pdf_total_incident_points: pdfTotal,
                    pdf_non_offtrack_incidents: suggestedDeduction,
                    pdf_offtracks_before_finish: Number(pdfRow.pdf_offtracks_before_finish || 0),
                    pdf_post_finish_1x: Number(pdfRow.pdf_post_finish_1x || 0),
                    pdf_post_finish_incident_points: Number(pdfRow.pdf_post_finish_incident_points || 0),
                    pdf_across_line_seen: Boolean(pdfRow.across_line_seen),
                    pdf_unsupported_incident_events: Array.isArray(pdfRow.unsupported_incident_events)
                        ? pdfRow.unsupported_incident_events
                        : [],
                    non_offtrack_incidents: canApply
                        ? String(suggestedDeduction)
                        : row.non_offtrack_incidents,
                    offtracks: canApply
                        ? Math.max(total - suggestedDeduction, 0)
                        : null,
                    offtrack_penalty_seconds: null,
                };
            });
            setIncidentRows(nextIncidentRows);

            setIncidentPdfSummary({
                ...(data?.summary || {}),
                filename: data?.filename || nextFile.name,
                pdf_driver_count: Number(data?.driver_count || pdfRows.length || 0),
                matched,
                auto_applied: autoApplied,
                mismatches,
                manual_review: manualReview,
                unmatched,
            });

            pushUi(
                `iRaceControl-PDF analysiert: ${matched} Fahrer zugeordnet, ${autoApplied} automatisch vorbelegt` +
                `${mismatches ? `, ${mismatches} Abweichungen` : ""}` +
                `${manualReview ? `, ${manualReview} manuell prüfen` : ""}.`,
            );
        } catch (err) {
            setError(`iRaceControl-PDF konnte nicht ausgewertet werden: ${prettyErr(err)}`);
        } finally {
            setIncidentPdfBusy(false);
        }
    }

    async function uploadSelectedFile() {
        if (uploadId) return uploadId;

        if (!file) {
            throw new Error("Bitte zuerst eine JSON Datei auswaehlen.");
        }

        setUploadBusy(true);
        pushUi("JSON wird an das Backend uebergeben...");
        try {
            const result = await uploadIracingJson(file);
            if (!result?.upload_id) throw new Error("Upload fehlgeschlagen: upload_id fehlt.");
            setUploadId(result.upload_id);
            setUploadName(result.filename || file.name);
            pushUi(`Datei bereit: ${result.upload_id}`);
            return result.upload_id;
        } finally {
            setUploadBusy(false);
        }
    }

    async function onRunImport() {
        setError("");
        setSummary(null);
        setJobId(null);
        setProgress({ status: "", step: 0, total: 0, label: "" });
        setLogItems([]);
        stopStream();
        stopPolling();

        if (!selectedSeason) return setError("Bitte Saison auswaehlen.");
        if (!selectedRace) return setError("Bitte Rennen auswaehlen.");

        setImportBusy(true);
        try {
            const resolvedUploadId = await uploadSelectedFile();
            pushUi("DB Pipeline Job wird gestartet (Import + Punkte + Standings)...");
            const result = await runResultsImportDb({
                upload_id: resolvedUploadId,
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                race_id: selectedRace.id,
                race_tag: selectedRace.race_tag,
            });

            if (!result?.job_id) throw new Error("Backend hat keine job_id geliefert.");

            setJobId(result.job_id);
            pushUi(`Job gestartet: #${result.job_id}`);
            startPolling(result.job_id);

            stopStreamRef.current = streamJobLogs({
                job_id: result.job_id,
                onLog: pushLog,
                onDone: async (status) => {
                    pushUi(`Job beendet: ${status}`);
                    try {
                        await pollJob(result.job_id);
                    } catch {}
                    if (String(status).trim() === "error") {
                        setError("Job beendet mit Fehler. Siehe Logs.");
                    }
                    setImportBusy(false);
                    stopStream();
                    stopPolling();
                },
                onError: (err) => {
                    const msg = prettyErr(err);
                    pushUi(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setImportBusy(false);
                    stopStream();
                    stopPolling();
                },
            });
        } catch (err) {
            const msg = prettyErr(err);
            setError(msg);
            pushUi(`FEHLER: ${msg}`);
            setImportBusy(false);
            stopStream();
            stopPolling();
        }
    }


    async function onRefreshStandings() {
        setError("");
        setSummary(null);
        setJobId(null);
        setProgress({ status: "", step: 0, total: 0, label: "" });
        setLogItems([]);
        stopStream();
        stopPolling();

        if (!selectedSeason) return setError("Bitte Saison auswaehlen.");
        if (!selectedRace) return setError("Bitte das letzte gewertete Rennen auswaehlen.");

        setStandingsRefreshBusy(true);
        try {
            pushUi(
                `Standings und Streicher werden bis ${selectedRace.race_tag || `Race #${selectedRace.id}`} neu berechnet...`
            );

            const result = await runStandingsSnapshotDb({
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                through_race_id: selectedRace.id,
                through_race_tag: selectedRace.race_tag,
            });

            if (!result?.job_id) throw new Error("Backend hat keine job_id geliefert.");

            setJobId(result.job_id);
            pushUi(`Standings-Refresh gestartet: #${result.job_id}`);
            startPolling(result.job_id);

            stopStreamRef.current = streamJobLogs({
                job_id: result.job_id,
                onLog: pushLog,
                onDone: async (status) => {
                    pushUi(`Standings-Refresh beendet: ${status}`);
                    try {
                        await pollJob(result.job_id);
                    } catch {}
                    if (String(status).trim() === "error") {
                        setError("Standings-Refresh beendet mit Fehler. Siehe Logs.");
                    }
                    setStandingsRefreshBusy(false);
                    stopStream();
                    stopPolling();
                },
                onError: (err) => {
                    const msg = prettyErr(err);
                    pushUi(`SSE FEHLER: ${msg}`);
                    setError(msg);
                    setStandingsRefreshBusy(false);
                    stopStream();
                    stopPolling();
                },
            });
        } catch (err) {
            const msg = prettyErr(err);
            setError(msg);
            pushUi(`FEHLER: ${msg}`);
            setStandingsRefreshBusy(false);
            stopStream();
            stopPolling();
        }
    }

    async function loadManualRows() {
        if (!selectedSeason || !selectedRace) return;
        setError("");
        setManualLoadBusy(true);
        try {
            const data = await fetchResultsImportDbRows({
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                race_id: selectedRace.id,
                race_tag: selectedRace.race_tag,
                result_import_id: summary?.result_import_id || null,
                session_type: "RACE",
            });

            const rows = Array.isArray(data?.rows) ? data.rows : [];
            setManualRows(
                rows.map((row) => ({
                    session_result_id: row.id,
                    driver_name_snapshot: row.driver_name_snapshot,
                    class_key: row.class_key,
                    car_number_snapshot: row.car_number_snapshot,
                    raw_laps_complete: row.raw_laps_complete || 0,
                    final_position_overall: row.final_position_overall,
                    final_position_class: row.final_position_class,
                    raw_finish_position_overall: row.raw_finish_position_overall,
                    raw_finish_position_class: row.raw_finish_position_class,
                    raw_gap_display: row.raw_gap_display || "",
                    raw_interval_display: row.raw_interval_display || "",
                    // Input field should reflect only manual overrides, not computed final intervals.
                    final_interval_display_overall: normalizeIntervalDisplay(row.manual_interval_display_overall || ""),
                    final_interval_display_class: normalizeIntervalDisplay(row.manual_interval_display_class || ""),
                })),
            );

            setIncidentRows((prev) => {
                const prevByKey = new Map((prev || []).map((row) => [incidentRowMatchKey(row), row]));
                return sortDriversByCarNumber(
                    rows.map((row) => {
                        const key = incidentRowMatchKey({
                            car_number: row.car_number_snapshot,
                            driver_name: row.driver_name_snapshot,
                        });
                        const previous = prevByKey.get(key);
                        const savedManual = Number(row.manual_non_offtrack_incidents || 0);
                        const savedOfftracks = row.offtrack_count;

                        return {
                            ...(previous || {}),
                            preview_key: `db-${row.id}`,
                            session_result_id: row.id,
                            driver_name: row.driver_name_snapshot,
                            car_number: row.car_number_snapshot || "",
                            total_incidents: Math.max(Number(row.raw_incidents || 0) || 0, 0),
                            non_offtrack_incidents: String(
                                previous?.non_offtrack_incidents !== undefined
                                    ? previous.non_offtrack_incidents
                                    : savedManual,
                            ),
                            offtracks:
                                savedOfftracks === null || savedOfftracks === undefined
                                    ? previous?.offtracks ?? null
                                    : Number(savedOfftracks),
                            offtrack_penalty_seconds: Number(row.offtrack_penalty_seconds || 0),
                        };
                    }),
                );
            });
            setIncidentCalculated(
                rows.length > 0 && rows.every((row) => row.offtrack_count !== null && row.offtrack_count !== undefined),
            );
        } catch (err) {
            setError(prettyErr(err));
        } finally {
            setManualLoadBusy(false);
        }
    }

    function onOpenIncidentPdfTest() {
        const adminMode = window.location.pathname.startsWith("/manager/admin/");
        const path = adminMode
            ? "/manager/admin/results-import/pdf-test"
            : "/manager/orga/results-import/pdf-test";

        const testWindow = window.open(
            path,
            "rv-iracecontrol-pdf-test",
            "width=1500,height=950,resizable=yes,scrollbars=yes",
        );

        if (testWindow) {
            testWindow.focus();
        }
    }

    function onManualRowChange(sessionResultId, key, value) {
        setManualRows((prev) =>
            prev.map((row) =>
                Number(row.session_result_id) === Number(sessionResultId)
                    ? { ...row, [key]: value }
                    : row,
            ),
        );
    }

    async function onSaveManualOverrides() {
        if (!selectedSeason || !selectedRace) return;
        setError("");
        setManualSummary(null);
        setManualSaveBusy(true);

        try {
            const payloadRows = manualRowsVisible.map((row) => ({
                session_result_id: row.session_result_id,
                final_interval_display: normalizeIntervalDisplay(row.final_interval_display_overall) || null,
            }));

            const data = await saveResultsImportManualOverrides({
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                race_id: selectedRace.id,
                race_tag: selectedRace.race_tag,
                result_import_id: summary?.result_import_id || null,
                manual_scope: "OVERALL",
                rows: payloadRows,
            });

            setManualSummary(data || null);
            pushUi("Manuelle Overall-Intervalle gespeichert. Klassenintervalle werden automatisch abgeleitet.");
            await loadManualRows();
        } catch (err) {
            const msg = prettyErr(err);
            setError(msg);
            pushUi(`FEHLER: ${msg}`);
        } finally {
            setManualSaveBusy(false);
        }
    }

    function onIncidentManualChange(rowKey, value) {
        const clean = String(value ?? "").replace(/[^0-9]/g, "");
        setIncidentCalculated(false);
        setOfftrackSummary(null);
        setIncidentRows((prev) =>
            prev.map((row) =>
                String(row.preview_key) === String(rowKey)
                    ? { ...row, non_offtrack_incidents: clean, offtracks: null, offtrack_penalty_seconds: null }
                    : row,
            ),
        );
    }

    function onCalculateOfftracks() {
        if (!incidentRows.length) {
            setError("Keine RACE-Incidents zum Berechnen vorhanden.");
            return;
        }

        let validationError = "";
        const nextRows = incidentRows.map((row) => {
            const total = Math.max(Number(row.total_incidents || 0) || 0, 0);
            const nonOfftrack = Math.max(Number(row.non_offtrack_incidents || 0) || 0, 0);
            if (nonOfftrack > total && !validationError) {
                validationError = `${row.driver_name}: Nicht-Offtrack-Abzug (${nonOfftrack}) darf nicht groesser als Gesamtincidents (${total}) sein.`;
            }
            return {
                ...row,
                non_offtrack_incidents: String(nonOfftrack),
                offtracks: Math.max(total - nonOfftrack, 0),
            };
        });

        if (validationError) {
            setError(validationError);
            setIncidentCalculated(false);
            return;
        }

        setError("");
        setIncidentRows(nextRows);
        setIncidentCalculated(true);
        pushUi("Bereinigte Offtracks wurden aus Gesamtincidents minus Nicht-Offtrack-Abzug berechnet.");
    }

    async function onReevaluateOfftrackResults() {
        if (!selectedSeason || !selectedRace) return setError("Bitte Saison und Rennen auswaehlen.");
        if (!incidentRows.length) return setError("Keine Incident-Daten vorhanden.");
        if (!incidentCalculated) return setError("Bitte zuerst die Offtracks berechnen.");
        if (incidentRows.some((row) => !row.session_result_id)) {
            return setError("Bitte zuerst Import + Punkte + Standings ausfuehren, damit die JSON-Fahrer den DB-Ergebniszeilen zugeordnet sind.");
        }

        setError("");
        setOfftrackSummary(null);
        setOfftrackBusy(true);
        try {
            const data = await runOfftrackRecalculationDb({
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                race_id: selectedRace.id,
                race_tag: selectedRace.race_tag,
                result_import_id: summary?.result_import_id || null,
                rows: incidentRows.map((row) => ({
                    session_result_id: row.session_result_id,
                    non_offtrack_incidents: Number(row.non_offtrack_incidents || 0),
                })),
            });

            setOfftrackSummary(data || null);
            const returnedRows = Array.isArray(data?.rows) ? data.rows : [];
            const byId = new Map(returnedRows.map((row) => [Number(row.session_result_id), row]));
            setIncidentRows((prev) =>
                prev.map((row) => {
                    const updated = byId.get(Number(row.session_result_id));
                    if (!updated) return row;
                    return {
                        ...row,
                        total_incidents: Number(updated.raw_incidents || 0),
                        non_offtrack_incidents: String(updated.non_offtrack_incidents || 0),
                        offtracks: Number(updated.offtracks || 0),
                        offtrack_penalty_seconds: Number(updated.offtrack_penalty_seconds || 0),
                    };
                }),
            );
            setIncidentCalculated(true);
            pushUi("Offtrack-Strafen angewendet. Positionen, Intervalle, Punkte, Standings und Snapshots wurden neu berechnet.");
            await loadManualRows();
        } catch (err) {
            const msg = prettyErr(err);
            setError(msg);
            pushUi(`FEHLER: ${msg}`);
        } finally {
            setOfftrackBusy(false);
        }
    }

    async function onApplyOrgaPenalty() {
        if (!selectedSeason || !selectedRace) return setError("Bitte Saison und Rennen auswaehlen.");
        if (!orgaPenaltyDriverId) return setError("Bitte Fahrer auswaehlen.");

        const penaltyType = String(orgaPenaltyType || "").trim().toUpperCase();
        const secondsValue = Number(orgaPenaltySeconds || 0);
        const pointsValue = Number(orgaPenaltyPoints || 0);

        if (penaltyType === "TIME_PENALTY" && !(secondsValue > 0)) {
            return setError("Zeitstrafe braucht Strafsekunden > 0.");
        }

        if (penaltyType === "TIME_CREDIT" && !(secondsValue > 0)) {
            return setError("Zeitgutschrift braucht Korrektursekunden > 0.");
        }

        if (penaltyType === "POINTS_PENALTY" && !(pointsValue > 0)) {
            return setError("Punktestrafe braucht Strafpunkte > 0.");
        }

        if (penaltyType === "TIME_AND_POINTS" && !(secondsValue > 0) && !(pointsValue > 0)) {
            return setError("Zeit + Punkte braucht Strafsekunden oder Strafpunkte > 0.");
        }

        setError("");
        setOrgaPenaltySummary(null);
        setOrgaPenaltyBusy(true);
        try {
            const data = await runOrgaPenaltyDb({
                series_key: seriesKey,
                season_id: selectedSeason.id,
                season_key: selectedSeason.season_key,
                race_id: selectedRace.id,
                race_tag: selectedRace.race_tag,
                result_import_id: summary?.result_import_id || null,
                penalized_season_driver_id: Number(orgaPenaltyDriverId),
                penalty_type: penaltyType,
                penalty_seconds: secondsValue,
                penalty_points: pointsValue,
                reason: orgaPenaltyReason,
            });

            setOrgaPenaltySummary(data || null);
            pushUi(
                penaltyType === "TIME_CREDIT"
                    ? "Zeitgutschrift gespeichert. Positionen, GAP, Intervalle, Punkte und Standings wurden neu berechnet."
                    : "ORGA Strafe gespeichert. Positionen, Intervalle, Punkte und Standings wurden neu berechnet."
            );
            await loadManualRows();
        } catch (err) {
            const msg = prettyErr(err);
            setError(msg);
            pushUi(`FEHLER: ${msg}`);
        } finally {
            setOrgaPenaltyBusy(false);
        }
    }

    const sessionSummary = summary?.sessions || {};
    const manualRowsVisible = useMemo(() => {
        function withCalculatedGapAndInterval(rows, leaderGapSeed = null) {
            if (!rows.length) return [];
            const leaderLaps = Number(rows[0].raw_laps_complete || 0);
            const leaderGap = leaderGapSeed ?? parseGapSeconds(rows[0].raw_gap_display);
            let prevRawGap = leaderGap;
            let prevLaps = leaderLaps;

            return rows.map((row, idx) => {
                const laps = Number(row.raw_laps_complete || 0);
                const rawGapSec = parseGapSeconds(row.raw_gap_display);
                let viewGap = "";
                let viewInterval = "";

                if (idx === 0) {
                    viewGap = "0:00.000";
                    viewInterval = "";
                } else if (laps < leaderLaps) {
                    viewGap = `-${Math.max(leaderLaps - laps, 1)}L`;
                    viewInterval = "";
                } else if (rawGapSec !== null && leaderGap !== null) {
                    const relativeGap = Math.max(rawGapSec - leaderGap, 0);
                    viewGap = formatGapSeconds(relativeGap);
                    if (prevRawGap !== null && laps === prevLaps) {
                        viewInterval = formatGapSeconds(Math.max(rawGapSec - prevRawGap, 0));
                    } else {
                        viewInterval = formatGapSeconds(relativeGap);
                    }
                } else {
                    viewGap = row.raw_gap_display || "";
                    viewInterval = row.raw_interval_display || "";
                }

                if (rawGapSec !== null) prevRawGap = rawGapSec;
                prevLaps = laps;

                return {
                    ...row,
                    view_raw_gap_display: viewGap,
                    view_raw_interval_display: viewInterval,
                };
            });
        }

        const sortedOverall = [...manualRows].sort((a, b) => {
            const aPos = Number(a.final_position_overall || a.raw_finish_position_overall || 999999);
            const bPos = Number(b.final_position_overall || b.raw_finish_position_overall || 999999);

            if (aPos !== bPos) return aPos - bPos;

            return Number(a.session_result_id) - Number(b.session_result_id);
        });

        return withCalculatedGapAndInterval(sortedOverall).map((row) => ({
            ...row,
            view_pos: row.final_position_overall || row.raw_finish_position_overall || "-",
            edit_interval_display: String(row.final_interval_display_overall || ""),
        }));
    }, [manualRows]);

    return (
        <div className="rv-page manager-modern-page">
            <section className="rv-page-hero">
                <div className="rv-page-hero__inner">
                    <div>
                        <div className="rv-page-kicker">Orga</div>
                        <h1 className="rv-page-title">Results importieren</h1>
                        <p className="rv-page-subtitle">
                            DB-only Bereich fuer Result Import und Punkteberechnung. Der bestehende Google-Sheets Standings Import bleibt unberuehrt.
                        </p>
                    </div>
                </div>
            </section>

            {error && <div className="rv-message rv-message--error">{error}</div>}

            <div className="card rv-filter-card" style={{ display: "grid", gap: 12 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontWeight: 800, fontSize: 13 }}>Serie</span>
                        <select className="input" value={seriesKey} onChange={(event) => onSeriesChange(event.target.value)} disabled={busy}>
                            {seriesRows.map((series) => (
                                <option key={series.id} value={series.series_key}>
                                    {series.display_name}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontWeight: 800, fontSize: 13 }}>Saison</span>
                        <select className="input" value={seasonId} onChange={(event) => setSeasonId(event.target.value)} disabled={busy || !seasons.length}>
                            {seasons.map((season) => (
                                <option key={season.id} value={season.id}>
                                    {season.display_name} ({season.status})
                                </option>
                            ))}
                        </select>
                    </label>
                    <label style={{ display: "grid", gap: 6 }}>
                        <span style={{ fontWeight: 800, fontSize: 13 }}>Rennen</span>
                        <select className="input" value={raceId} onChange={(event) => setRaceId(event.target.value)} disabled={busy || !races.length}>
                            {races.map((race) => (
                                <option key={race.id} value={race.id}>
                                    {race.race_tag} - {race.track_name}
                                </option>
                            ))}
                        </select>
                    </label>
                </div>
            </div>

            <div style={{ display: "grid", gap: 14 }}>
                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>1. Import + Punkte + Standings</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        Startet den kompletten DB-Workflow im Worker in einem Lauf: JSON Import, Punkteberechnung und Standings Snapshot.
                    </p>

                    <div style={sectionStyle}>
                        <div style={{ display: "grid", gap: 8 }}>
                            <strong>iRacing JSON Datei</strong>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <input
                                    id="rv-results-json-upload"
                                    type="file"
                                    accept=".json,application/json"
                                    onChange={(event) => onFileChange(event.target.files?.[0] || null)}
                                    style={{ display: "none" }}
                                    disabled={busy}
                                />
                                <label htmlFor="rv-results-json-upload" className="btn" style={{ cursor: busy ? "not-allowed" : "pointer", opacity: busy ? 0.6 : 1 }}>
                                    {file ? "Datei aendern" : "Datei auswaehlen"}
                                </label>
                                <div
                                    style={{
                                        padding: "9px 12px",
                                        borderRadius: 12,
                                        border: "1px solid rgba(255,255,255,.14)",
                                        background: "rgba(255,255,255,.03)",
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={fileLabel}
                                >
                                    {fileLabel}
                                </div>
                            </div>
                            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                                <button className="btn btn--accent" onClick={onRunImport} disabled={busy || (!file && !uploadId) || !selectedSeason || !selectedRace}>
                                    {uploadBusy || importBusy ? "Verarbeite..." : "Import + Punkte + Standings starten"}
                                </button>

                                <button
                                    className="btn"
                                    onClick={onRefreshStandings}
                                    disabled={busy || !selectedSeason || !selectedRace}
                                    title="Berechnet den Standings-Snapshot und die Streicher aus den bereits gespeicherten Rennpunkten neu. Kein JSON-Upload erforderlich."
                                >
                                    {standingsRefreshBusy ? "Aktualisiere..." : "Standings / Streicher aktualisieren"}
                                </button>
                            </div>
                            {uploadId && (
                                <div className="muted">
                                    Datei bereit: <b>{uploadName}</b> - ID: <code>{uploadId}</code>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>2. Incidents manuell eintragen</h2>

                    <p className="muted" style={{ margin: 0 }}>
                        Aus der eingelesenen iRacing JSON werden ausschliesslich die Incidents der Session <b>RACE</b> uebernommen.
                        Du kannst die Werte weiterhin komplett manuell pflegen. Optional kann ein <b>iRaceControl Race Report als PDF</b> vorab eingelesen werden:
                        RaceVision erkennt 1x, 2x, 4x und Upgrades wie 1x-&gt;2x automatisch und befuellt den manuellen Abzug vor.
                        Ein <b>1x nach „Across the line!“</b> wird dabei automatisch als nicht zu wertender Offtrack abgezogen.
                    </p>

                    {incidentFileBusy && (
                        <div style={sectionStyle}>
                            <span className="muted">RACE-Incidents werden aus der JSON gelesen...</span>
                        </div>
                    )}

                    <div style={sectionStyle}>
                        <div style={{ display: "grid", gap: 8 }}>
                            <strong>Optional: iRaceControl PDF automatisch auswerten</strong>
                            <div className="muted" style={{ fontSize: 13 }}>
                                Das PDF wird nur zur Vorbelegung verwendet. Die Spalte <b>2x | 4x / Abzug</b> bleibt danach frei editierbar.
                                Automatisch uebernommen wird nur, wenn Fahrzeugnummer und Gesamtincidents eindeutig zur JSON passen.
                            </div>
                            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                                <input
                                    id="rv-results-iracecontrol-pdf"
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={(event) => onIncidentPdfChange(event.target.files?.[0] || null)}
                                    style={{ display: "none" }}
                                    disabled={busy || !incidentRows.length}
                                />
                                <label
                                    htmlFor="rv-results-iracecontrol-pdf"
                                    className="btn"
                                    style={{ cursor: busy || !incidentRows.length ? "not-allowed" : "pointer", opacity: busy || !incidentRows.length ? 0.6 : 1 }}
                                >
                                    {incidentPdfBusy ? "PDF wird analysiert..." : incidentPdfFile ? "PDF aendern" : "iRaceControl PDF einlesen"}
                                </label>
                                <button
                                    className="btn"
                                    type="button"
                                    onClick={onOpenIncidentPdfTest}
                                    disabled={incidentPdfBusy}
                                    title="Öffnet einen separaten Testmodus für alte iRaceControl-PDFs. Es werden keine Renndaten gespeichert oder verändert."
                                >
                                    PDF-Test
                                </button>
                                <div
                                    style={{
                                        padding: "9px 12px",
                                        borderRadius: 12,
                                        border: "1px solid rgba(255,255,255,.14)",
                                        background: "rgba(255,255,255,.03)",
                                        maxWidth: "100%",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                    title={incidentPdfFile?.name || ""}
                                >
                                    {incidentPdfFile?.name || "Kein PDF geladen"}
                                </div>
                            </div>

                            {!!incidentPdfSummary && (
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13 }}>
                                    <span>PDF-Fahrer: <b>{incidentPdfSummary.pdf_driver_count ?? 0}</b></span>
                                    <span>Zugeordnet: <b>{incidentPdfSummary.matched ?? 0}</b></span>
                                    <span>Automatisch vorbelegt: <b>{incidentPdfSummary.auto_applied ?? 0}</b></span>
                                    <span>Abweichungen: <b>{incidentPdfSummary.mismatches ?? 0}</b></span>
                                    <span>Manuell pruefen: <b>{incidentPdfSummary.manual_review ?? 0}</b></span>
                                    <span>1x nach Ziel ignoriert: <b>{incidentPdfSummary.post_finish_1x_ignored ?? 0}</b></span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <table className="table table--wide" style={{ minWidth: 1180 }}>
                            <thead>
                            <tr>
                                <th>Fahrer</th>
                                <th>Gesamtincidents</th>
                                <th>PDF Race</th>
                                <th>Nach Ziel</th>
                                <th>2x | 4x / Abzug</th>
                                <th>Offtracks</th>
                                <th>Pruefung</th>
                            </tr>
                            </thead>
                            <tbody>
                            {!incidentRows.length && (
                                <tr>
                                    <td colSpan={7} className="muted">
                                        Noch keine RACE-Incidents geladen. JSON auswaehlen oder vorhandene Result Rows laden.
                                    </td>
                                </tr>
                            )}

                            {incidentRows.map((row) => {
                                const pdfStatus = pdfIncidentStatus(row);
                                const transitionParts = [
                                    Number(row.pdf_1x_to_2x || 0) > 0 ? `1x→2x: ${row.pdf_1x_to_2x}` : "",
                                    Number(row.pdf_1x_to_4x || 0) > 0 ? `1x→4x: ${row.pdf_1x_to_4x}` : "",
                                    Number(row.pdf_2x_to_4x || 0) > 0 ? `2x→4x: ${row.pdf_2x_to_4x}` : "",
                                    Number(row.pdf_3x || 0) > 0 ? `3x: ${row.pdf_3x}` : "",
                                ].filter(Boolean);

                                return (
                                    <tr key={row.preview_key}>
                                        <td>
                                            {row.car_number ? `#${row.car_number} ` : ""}
                                            <strong>{row.driver_name}</strong>
                                        </td>
                                        <td><strong>{row.total_incidents}</strong></td>
                                        <td style={{ minWidth: 220 }}>
                                            {!row.pdf_loaded ? "-" : !row.pdf_matched ? (
                                                <span className="muted">Kein Match</span>
                                            ) : (
                                                <div style={{ display: "grid", gap: 3, fontSize: 13 }}>
                                                    <span>1x: <b>{row.pdf_1x ?? 0}</b> · 2x: <b>{row.pdf_2x ?? 0}</b> · 4x: <b>{row.pdf_4x ?? 0}</b></span>
                                                    {!!transitionParts.length && <span className="muted">{transitionParts.join(" · ")}</span>}
                                                    <span className="muted">PDF-Punkte: {row.pdf_total_incident_points ?? 0}</span>
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ minWidth: 135 }}>
                                            {!row.pdf_matched ? "-" : Number(row.pdf_post_finish_1x || 0) > 0 ? (
                                                <div>
                                                    <strong>{row.pdf_post_finish_1x}× 1x</strong>
                                                    <div className="muted" style={{ fontSize: 12 }}>nicht als Offtrack</div>
                                                </div>
                                            ) : (
                                                <span className="muted">0</span>
                                            )}
                                        </td>
                                        <td style={{ minWidth: 170 }}>
                                            <input
                                                className="input"
                                                type="number"
                                                min="0"
                                                max={row.total_incidents}
                                                step="1"
                                                value={row.non_offtrack_incidents}
                                                onChange={(event) => onIncidentManualChange(row.preview_key, event.target.value)}
                                                disabled={busy}
                                                aria-label={`Nicht-Offtrack-Incidents fuer ${row.driver_name}`}
                                            />
                                            {row.pdf_matched && (
                                                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                                                    PDF-Vorschlag: {row.pdf_non_offtrack_incidents ?? 0}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            {row.offtracks === null || row.offtracks === undefined ? "-" : (
                                                <strong>{row.offtracks}</strong>
                                            )}
                                            {Number(row.offtrack_penalty_seconds || 0) > 0 && (
                                                <div className="muted" style={{ fontSize: 12, marginTop: 3 }}>
                                                    +{Number(row.offtrack_penalty_seconds).toLocaleString("de-DE", { maximumFractionDigits: 3 })}s Zeitstrafe
                                                </div>
                                            )}
                                        </td>
                                        <td style={{ minWidth: 180 }}>
                                            <span
                                                style={{
                                                    fontSize: 12,
                                                    fontWeight: 800,
                                                    opacity: pdfStatus.tone === "muted" ? 0.65 : 1,
                                                }}
                                            >
                                                {pdfStatus.label}
                                            </span>
                                            {row.pdf_matched && row.pdf_auto_safe && row.pdf_total_matches_json && (
                                                <div className="muted" style={{ fontSize: 11, marginTop: 4 }}>
                                                    PDF-Offtracks bis Ziel: {row.pdf_offtracks_before_finish ?? 0}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <button
                            className="btn"
                            type="button"
                            onClick={onCalculateOfftracks}
                            disabled={busy || !incidentRows.length}
                        >
                            Offtracks berechnen
                        </button>
                        <button
                            className="btn btn--accent"
                            type="button"
                            onClick={onReevaluateOfftrackResults}
                            disabled={busy || !incidentRows.length || !incidentCalculated || incidentRows.some((row) => !row.session_result_id)}
                        >
                            {offtrackBusy ? "Auswertung läuft..." : "Ergebnisse neu auswerten"}
                        </button>
                    </div>

                    {incidentRows.length > 0 && incidentRows.some((row) => !row.session_result_id) && (
                        <div className="muted" style={{ fontSize: 13 }}>
                            Die Tabelle kann direkt aus der JSON vorbereitet werden. Fuer „Ergebnisse neu auswerten“ muss der DB-Import oben einmal abgeschlossen sein.
                        </div>
                    )}

                    {!!offtrackSummary?.ok && (
                        <div style={sectionStyle}>
                            <b>Offtrack-Auswertung angewendet</b>
                            <div style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap", opacity: 0.92 }}>
                                <span>Fahrer mit Strafe: {offtrackSummary.penalized_drivers ?? 0}</span>
                                <span>Offtrack-Strafen: {offtrackSummary.created_adjustments ?? 0}</span>
                                <span>Snapshots: {offtrackSummary?.standings?.snapshot_count ?? 0}</span>
                            </div>
                            {!!offtrackSummary?.rules && (
                                <div className="muted" style={{ marginTop: 8 }}>
                                    Regel: ab {offtrackSummary.rules.initial_threshold} Offtracks = +{offtrackSummary.rules.initial_penalty_seconds}s;
                                    danach alle {offtrackSummary.rules.interval} weiteren Offtracks jeweils +{offtrackSummary.rules.increment_penalty_seconds}s.
                                </div>
                            )}
                        </div>
                    )}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>3. Overall-Intervall manuell pflegen</h2>

                    <p className="muted" style={{ margin: 0 }}>
                        Du pflegst hier nur noch die Gesamt-/Overall-Intervalle. PRO/INT/AM werden daraus automatisch berechnet,
                        indem alle Overall-Intervalle zwischen zwei Klassenfahrern addiert werden.
                    </p>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
                        <div className="pill pill--gesamt" style={{ alignSelf: "center" }}>
                            Ansicht: Gesamt
                        </div>

                        <button className="btn" onClick={loadManualRows} disabled={busy || !selectedSeason || !selectedRace}>
                            {manualLoadBusy ? "Laden..." : "Result Rows laden"}
                        </button>

                        <button
                            className="btn btn--accent"
                            onClick={onSaveManualOverrides}
                            disabled={busy || !manualRowsVisible.length || !selectedSeason || !selectedRace}
                        >
                            {manualSaveBusy ? "Speichern..." : "Overall-Intervalle speichern"}
                        </button>
                    </div>

                    {!!manualSummary && (
                        <div style={sectionStyle}>
                            <b>Manual Overrides gespeichert</b>
                            <div style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap", opacity: 0.92 }}>
                                <span>created: {manualSummary.created ?? 0}</span>
                                <span>updated: {manualSummary.updated ?? 0}</span>
                                <span>deactivated: {manualSummary.deactivated ?? 0}</span>
                                <span>rows: {manualSummary?.adjustments?.rows ?? 0}</span>
                                <span>active adjustments: {manualSummary?.adjustments?.adjustments ?? 0}</span>
                            </div>
                        </div>
                    )}

                    <div style={{ overflowX: "auto" }}>
                        <table className="table table--wide">
                            <thead>
                            <tr>
                                <th>Pos</th>
                                <th>Fahrer</th>
                                <th>Klasse</th>
                                <th>Raw GAP</th>
                                <th>Raw Intervall</th>
                                <th>Final Intervall</th>
                            </tr>
                            </thead>

                            <tbody>
                            {!manualRowsVisible.length && (
                                <tr>
                                    <td colSpan={6} className="muted">
                                        Keine Rows geladen.
                                    </td>
                                </tr>
                            )}

                            {manualRowsVisible.map((row) => (
                                <tr key={row.session_result_id}>
                                    <td>{row.view_pos}</td>

                                    <td>
                                        {row.car_number_snapshot ? `#${row.car_number_snapshot} ` : ""}
                                        {row.driver_name_snapshot}
                                    </td>

                                    <td>{row.class_key || "-"}</td>

                                    <td>{row.view_raw_gap_display || "-"}</td>

                                    <td>{row.view_raw_interval_display || "-"}</td>

                                    <td>
                                        <input
                                            className="input"
                                            value={row.edit_interval_display}
                                            onChange={(event) =>
                                                onManualRowChange(
                                                    row.session_result_id,
                                                    "final_interval_display_overall",
                                                    event.target.value,
                                                )
                                            }
                                            onBlur={(event) =>
                                                onManualRowChange(
                                                    row.session_result_id,
                                                    "final_interval_display_overall",
                                                    normalizeIntervalDisplay(event.target.value),
                                                )
                                            }
                                            placeholder="z.B. 9.452 oder 2:14.321"
                                            disabled={busy}
                                        />
                                    </td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>4. Fehlende Abmeldungen</h2>

                    <p className="muted" style={{ margin: 0 }}>
                        Nach dem Import wird geprüft, welche startberechtigten Fahrer nicht gefahren sind und keine aktive Abmeldung hatten.
                        Wartelistenfahrer zählen mit, sobald sie durch Abmeldungen rechnerisch startberechtigt waren.
                    </p>

                    {!summary && (
                        <div style={sectionStyle}>
            <span className="muted">
                Die Prüfung erscheint nach dem Import + Punkte + Standings Workflow.
            </span>
                        </div>
                    )}

                    {summary && !absenceAuditAvailable && (
                        <div style={sectionStyle}>
            <span className="muted">
                Für diesen Import liegt noch keine Abmeldeprüfung vor. Bitte den Import einmal neu ausführen.
            </span>
                        </div>
                    )}

                    {absenceAuditAvailable && !unexcusedAbsences.length && (
                        <div
                            style={{
                                ...sectionStyle,
                                background: "rgba(139,217,166,.075)",
                                border: "1px solid rgba(139,217,166,.24)",
                            }}
                        >
                            <b>Keine fehlenden Abmeldungen gefunden.</b>
                            <div className="muted" style={{ marginTop: 6 }}>
                                Alle erwarteten Starter sind gefahren oder hatten eine aktive Abmeldung.
                            </div>
                        </div>
                    )}

                    {!!unexcusedAbsences.length && (
                        <div
                            style={{
                                ...sectionStyle,
                                background: "rgba(255,212,112,.075)",
                                border: "1px solid rgba(255,212,112,.28)",
                            }}
                        >
                            <b>Fehlende Abmeldungen gefunden: {unexcusedAbsences.length}</b>

                            <div style={{ overflowX: "auto", marginTop: 12 }}>
                                <table className="table table--wide">
                                    <thead>
                                    <tr>
                                        <th>Grid</th>
                                        <th>Fahrer</th>
                                        <th>Klasse</th>
                                        <th>Team</th>
                                        <th>Status</th>
                                    </tr>
                                    </thead>

                                    <tbody>
                                    {unexcusedAbsences.map((row, index) => (
                                        <tr key={row.season_driver_id || `${row.driver_name}-${index}`}>
                                            <td>
                                                {row.starter_type === "waitlist"
                                                    ? `W${row.grid_position}`
                                                    : `P${row.grid_position}`}
                                            </td>

                                            <td>
                                                {row.car_number ? `#${row.car_number} ` : ""}
                                                <strong>{row.driver_name}</strong>
                                            </td>

                                            <td>{row.class_name || "-"}</td>
                                            <td>{row.team_name || "-"}</td>

                                            <td>
                                                {row.starter_type === "waitlist"
                                                    ? "Warteliste war startberechtigt"
                                                    : "Stammfahrer"}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="muted" style={{ marginTop: 10 }}>
                                Hinweis: Die Liste ist eine Prüfhilfe. Strafen bitte darunter im ORGA-Strafen-Bereich bewusst setzen.
                            </div>
                        </div>
                    )}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <h2 style={{ margin: 0 }}>5. ORGA Strafen / Zeitkorrekturen</h2>
                    <p className="muted" style={{ margin: 0 }}>
                        Direkte Strafe oder Zeitgutschrift. Beispiel: iRacing wertet +40s, erlaubt sind nur +24s.
                        Dann hier „Zeitgutschrift / iRacing-Korrektur“ wählen und 16 Sekunden eintragen.
                        Ergebnis wird neu sortiert, GAP/Intervalle werden neu berechnet, Punkte und Standings aktualisiert.
                    </p>

                    <div style={sectionStyle}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                                <span style={{ fontWeight: 800, fontSize: 13 }}>Fahrer</span>
                                <select
                                    className="input rv-wide-native-select"
                                    value={orgaPenaltyDriverId}
                                    onChange={(event) => setOrgaPenaltyDriverId(event.target.value)}
                                    disabled={busy || !sortedSeasonDriverOptions.length}
                                >
                                    {sortedSeasonDriverOptions.map((row) => (
                                        <option key={row.id} value={row.id}>
                                            {driverDisplayLabel(row)}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label style={{ display: "grid", gap: 6 }}>
                                <span style={{ fontWeight: 800, fontSize: 13 }}>Strafart</span>
                                <select
                                    className="input"
                                    value={orgaPenaltyType}
                                    onChange={(event) => {
                                        const nextType = event.target.value;
                                        setOrgaPenaltyType(nextType);

                                        if (nextType === "TIME_CREDIT") {
                                            setOrgaPenaltyPoints("0");
                                            setOrgaPenaltyReason((prev) =>
                                                prev || "Korrektur iRacing Auto-Strafe"
                                            );
                                        }
                                    }}
                                    disabled={busy}
                                >
                                    {ORGA_PENALTY_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                            {opt.label}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label style={{ display: "grid", gap: 6 }}>
                                <span style={{ fontWeight: 800, fontSize: 13 }}>
                                    {orgaPenaltyType === "TIME_CREDIT" ? "Korrektursekunden" : "Strafsekunden"}</span>
                                <input
                                    className="input"
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={orgaPenaltySeconds}
                                    onChange={(event) => setOrgaPenaltySeconds(event.target.value)}
                                    disabled={busy}
                                />
                            </label>

                            <label style={{ display: "grid", gap: 6 }}>
                                <span style={{ fontWeight: 800, fontSize: 13 }}>Strafpunkte</span>
                                <input
                                    className="input"
                                    type="number"
                                    min="0"
                                    step="1"
                                    value={orgaPenaltyPoints}
                                    onChange={(event) => setOrgaPenaltyPoints(event.target.value)}
                                    disabled={busy}
                                />
                            </label>
                        </div>

                        <label style={{ display: "grid", gap: 6, marginTop: 10 }}>
                            <span style={{ fontWeight: 800, fontSize: 13 }}>Begruendung</span>
                            <input
                                className="input"
                                value={orgaPenaltyReason}
                                onChange={(event) => setOrgaPenaltyReason(event.target.value)}
                                placeholder="z.B. Fehlende Abmeldung"
                                disabled={busy}
                            />
                        </label>

                        <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                            <button
                                className="btn btn--accent"
                                onClick={onApplyOrgaPenalty}
                                disabled={busy || !selectedSeason || !selectedRace || !orgaPenaltyDriverId}
                            >
                                {orgaPenaltyBusy
                                    ? "Speichere..."
                                    : orgaPenaltyType === "TIME_CREDIT"
                                        ? "Zeitgutschrift anwenden"
                                        : "ORGA Strafe freigeben"}
                            </button>
                            {seasonDriverLoadBusy && <span className="muted">Fahrer werden geladen...</span>}
                        </div>
                    </div>

                    {!!orgaPenaltySummary?.ok && (
                        <div style={sectionStyle}>
                            <b>ORGA Strafe angewendet</b>
                            <div style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap", opacity: 0.92 }}>
                                <span>result_import_id: {orgaPenaltySummary.result_import_id}</span>
                                <span>session_result_id: {orgaPenaltySummary.session_result_id}</span>
                                <span>created: {orgaPenaltySummary.created_adjustments ?? 0}</span>
                                <span>deactivated: {orgaPenaltySummary.deactivated_adjustments ?? 0}</span>
                                <span>driver points: {orgaPenaltySummary?.race_points?.driver_point_rows ?? 0}</span>
                                <span>team points: {orgaPenaltySummary?.race_points?.team_point_rows ?? 0}</span>
                                <span>brand points: {orgaPenaltySummary?.race_points?.brand_point_rows ?? 0}</span>
                                <span>snapshots: {orgaPenaltySummary?.standings?.snapshot_count ?? 0}</span>
                            </div>
                        </div>
                    )}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
                        <h2 style={{ margin: 0 }}>Job & Status</h2>
                        {jobId && <span className="pill pill--gesamt">Job #{jobId}</span>}
                    </div>
                    <ProgressBar progress={progress} />

                    {summary?.ok && (
                        <div style={sectionStyle}>
                            <b>
                                {summary.job_type === "race_points_db"
                                    ? "Punkte gespeichert"
                                    : summary.job_type === "standings_snapshot_db"
                                        ? "Standings Snapshot gespeichert"
                                        : "Import + Punkte + Standings gespeichert"}
                            </b>
                            <div style={{ marginTop: 8, display: "flex", gap: 14, flexWrap: "wrap", opacity: 0.92 }}>
                                {summary.result_import_id !== undefined && <span>result_import_id: {summary.result_import_id}</span>}
                                {summary.season_id !== undefined && <span>season_id: {summary.season_id}</span>}
                                {summary.race_id !== undefined && <span>race_id: {summary.race_id}</span>}
                                {summary.through_race_id !== undefined && <span>through_race_id: {summary.through_race_id}</span>}
                                {summary.through_race_tag && <span>through_race_tag: {summary.through_race_tag}</span>}
                                {summary.import_version !== undefined && <span>version: {summary.import_version}</span>}
                                {summary.rows !== undefined && <span>rows: {summary.rows}</span>}
                                {summary.driver_point_rows !== undefined && <span>driver points: {summary.driver_point_rows}</span>}
                                {summary.team_point_rows !== undefined && <span>team points: {summary.team_point_rows}</span>}
                                {summary.brand_point_rows !== undefined && <span>brand points: {summary.brand_point_rows}</span>}
                                {summary.snapshot_count !== undefined && <span>snapshots: {summary.snapshot_count}</span>}
                                {summary.dns_count !== undefined && <span>DNS: {summary.dns_count}</span>}
                                {summary.dnf_count !== undefined && <span>DNF: {summary.dnf_count}</span>}
                                {summary.dsq_count !== undefined && <span>DSQ: {summary.dsq_count}</span>}
                                {summary.season_cars_set !== undefined && <span>Saisonautos gesetzt: {summary.season_cars_set}</span>}
                            </div>
                            {!!Object.keys(sessionSummary).length && (
                                <div style={{ marginTop: 8, opacity: 0.9 }}>
                                    Sessions: {Object.entries(sessionSummary).map(([key, value]) => `${key}: ${value}`).join(", ")}
                                </div>
                            )}
                        </div>
                    )}
                </section>

                <section className="card" style={{ display: "grid", gap: 12 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <h2 style={{ margin: 0 }}>Logs</h2>
                        <button className="btn" onClick={() => setLogItems([])} disabled={!logItems.length}>
                            Clear Logs
                        </button>
                    </div>
                    <div
                        ref={logsRef}
                        style={{
                            height: 360,
                            overflow: "auto",
                            border: "1px solid rgba(255,255,255,.14)",
                            borderRadius: 14,
                            padding: 12,
                            background: "rgba(0,0,0,.24)",
                            fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
                            fontSize: 12,
                            lineHeight: 1.45,
                            whiteSpace: "pre-wrap",
                        }}
                    >
                        {logItems.length ? (
                            logItems.map((item, index) => (
                                <div
                                    key={`${item.seq ?? "ui"}-${index}`}
                                    style={{
                                        color: item.level === "ERROR" ? "#ff8f8f" : item.level === "WARN" ? "#ffd27a" : "rgba(255,255,255,.86)",
                                        opacity: item.uiOnly ? 0.78 : 1,
                                    }}
                                >
                                    {item.seq ? `${item.seq}. ` : ""}
                                    {item.msg}
                                </div>
                            ))
                        ) : (
                            <div style={{ opacity: 0.55 }}>Noch keine Logs.</div>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
